import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Beaker, Droplets, Trophy, HelpCircle, RotateCcw, Star, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface PotionLabGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Potion {
  id: number;
  name: string;
  color: string;
  volume: number; // en mL
  maxVolume: number;
  currentVolume: number;
}

interface Recipe {
  id: number;
  name: string;
  description: string;
  ingredients: { potionId: number; volume: number }[];
  targetVolume: number;
  difficulty: number;
  magicEffect: string;
}

interface Challenge {
  recipe: Recipe;
  userMix: { potionId: number; volume: number }[];
  completed: boolean;
  score: number;
}

const PotionLabGame: React.FC<PotionLabGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [potions, setPotions] = useState<Potion[]>([]);
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null);
  const [pouringAmount, setPouringAmount] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [challengeCount, setChallengeCount] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerMode, setTimerMode] = useState(false);
  const [gameMode, setGameMode] = useState<'guided' | 'free'>('guided');
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to the Magic Potion Lab!' : 'Bienvenue au Laboratoire de Potions Magiques !',
      content: language === 'en' 
        ? 'Learn about volume measurements by creating magical potions! Mix precise amounts to create powerful spells.'
        : 'Apprenez les mesures de volume en créant des potions magiques ! Mélangez des quantités précises pour créer des sorts puissants.'
    },
    {
      title: language === 'en' ? 'Understanding Volume' : 'Comprendre le volume',
      content: language === 'en'
        ? 'Volume is measured in liters (L) and milliliters (mL). 1 liter = 1000 milliliters. Watch the graduations carefully!'
        : 'Le volume se mesure en litres (L) et millilitres (mL). 1 litre = 1000 millilitres. Observez bien les graduations !'
    },
    {
      title: language === 'en' ? 'Mixing Potions' : 'Mélanger les potions',
      content: language === 'en'
        ? 'Select a potion, enter the exact amount needed, and pour it into the cauldron. Follow the recipe precisely!'
        : 'Sélectionnez une potion, entrez la quantité exacte nécessaire, et versez-la dans le chaudron. Suivez la recette précisément !'
    }
  ];

  const initializePotions = (): Potion[] => {
    return [
      {
        id: 1,
        name: language === 'en' ? 'Dragon\'s Blood' : 'Sang de Dragon',
        color: '#DC2626',
        volume: 0,
        maxVolume: 2000,
        currentVolume: 2000
      },
      {
        id: 2,
        name: language === 'en' ? 'Fairy Tears' : 'Larmes de Fée',
        color: '#3B82F6',
        volume: 0,
        maxVolume: 1500,
        currentVolume: 1500
      },
      {
        id: 3,
        name: language === 'en' ? 'Unicorn Essence' : 'Essence de Licorne',
        color: '#A855F7',
        volume: 0,
        maxVolume: 1000,
        currentVolume: 1000
      },
      {
        id: 4,
        name: language === 'en' ? 'Phoenix Elixir' : 'Élixir de Phénix',
        color: '#F59E0B',
        volume: 0,
        maxVolume: 800,
        currentVolume: 800
      }
    ];
  };

  const generateRecipe = (): Recipe => {
    const recipes = {
      1: [ // Niveau 1 - Mesures simples
        {
          id: 1,
          name: language === 'en' ? 'Healing Potion' : 'Potion de Guérison',
          description: language === 'en' ? 'A simple healing brew' : 'Un simple breuvage de guérison',
          ingredients: [
            { potionId: 1, volume: 500 },
            { potionId: 2, volume: 500 }
          ],
          targetVolume: 1000,
          difficulty: 1,
          magicEffect: '✨'
        },
        {
          id: 2,
          name: language === 'en' ? 'Strength Elixir' : 'Élixir de Force',
          description: language === 'en' ? 'Increases physical power' : 'Augmente la force physique',
          ingredients: [
            { potionId: 1, volume: 750 },
            { potionId: 3, volume: 250 }
          ],
          targetVolume: 1000,
          difficulty: 1,
          magicEffect: '💪'
        }
      ],
      2: [ // Niveau 2 - Mesures composées
        {
          id: 3,
          name: language === 'en' ? 'Wisdom Brew' : 'Breuvage de Sagesse',
          description: language === 'en' ? 'Enhances mental abilities' : 'Améliore les capacités mentales',
          ingredients: [
            { potionId: 2, volume: 300 },
            { potionId: 3, volume: 450 },
            { potionId: 4, volume: 250 }
          ],
          targetVolume: 1000,
          difficulty: 2,
          magicEffect: '🧠'
        },
        {
          id: 4,
          name: language === 'en' ? 'Invisibility Potion' : 'Potion d\'Invisibilité',
          description: language === 'en' ? 'Makes you transparent' : 'Vous rend transparent',
          ingredients: [
            { potionId: 1, volume: 200 },
            { potionId: 2, volume: 350 },
            { potionId: 3, volume: 150 },
            { potionId: 4, volume: 300 }
          ],
          targetVolume: 1000,
          difficulty: 2,
          magicEffect: '👻'
        }
      ],
      3: [ // Niveau 3 - Conversions L/mL
        {
          id: 5,
          name: language === 'en' ? 'Master\'s Elixir' : 'Élixir du Maître',
          description: language === 'en' ? 'The ultimate magical brew (1.5L)' : 'Le breuvage magique ultime (1,5L)',
          ingredients: [
            { potionId: 1, volume: 600 },
            { potionId: 2, volume: 400 },
            { potionId: 3, volume: 300 },
            { potionId: 4, volume: 200 }
          ],
          targetVolume: 1500,
          difficulty: 3,
          magicEffect: '🌟'
        }
      ]
    };

    const levelRecipes = recipes[level as keyof typeof recipes];
    return levelRecipes[Math.floor(Math.random() * levelRecipes.length)];
  };

  const addIngredient = () => {
    if (!selectedPotion || !pouringAmount || !currentChallenge) return;
    
    const volume = parseInt(pouringAmount);
    if (isNaN(volume) || volume <= 0) return;
    
    const potion = potions.find(p => p.id === selectedPotion);
    if (!potion || volume > potion.currentVolume) return;
    
    // Update user mix
    const newMix = [...currentChallenge.userMix];
    const existingIndex = newMix.findIndex(m => m.potionId === selectedPotion);
    
    if (existingIndex >= 0) {
      newMix[existingIndex].volume += volume;
    } else {
      newMix.push({ potionId: selectedPotion, volume });
    }
    
    // Update potion current volume
    setPotions(prev => prev.map(p => 
      p.id === selectedPotion 
        ? { ...p, currentVolume: p.currentVolume - volume }
        : p
    ));
    
    setCurrentChallenge({
      ...currentChallenge,
      userMix: newMix
    });
    
    setPouringAmount('');
    setSelectedPotion(null);
  };

  const checkRecipe = () => {
    if (!currentChallenge) return;
    
    const { recipe, userMix } = currentChallenge;
    let correct = true;
    let tolerance = level === 1 ? 0 : level === 2 ? 25 : 50; // Tolérance en mL
    
    // Vérifier chaque ingrédient
    for (const ingredient of recipe.ingredients) {
      const userIngredient = userMix.find(m => m.potionId === ingredient.potionId);
      const userVolume = userIngredient?.volume || 0;
      
      if (Math.abs(userVolume - ingredient.volume) > tolerance) {
        correct = false;
        break;
      }
    }
    
    // Vérifier qu'il n'y a pas d'ingrédients en trop
    for (const userIngredient of userMix) {
      const recipeIngredient = recipe.ingredients.find(i => i.potionId === userIngredient.potionId);
      if (!recipeIngredient) {
        correct = false;
        break;
      }
    }
    
    setIsCorrect(correct);
    setShowFeedback(true);
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
    
    if (correct) {
      const points = recipe.difficulty * 10;
      setScore(score + points);
      playSuccess();
    } else {
      playError();
    }
    
    setTimeout(() => {
      setChallengeCount(challengeCount + 1);
      if (challengeCount >= 9) {
        setGameComplete(true);
        saveProgress(newStats);
      } else {
        if (challengeCount % 3 === 2 && level < 3) {
          setLevel(level + 1);
        }
        nextChallenge();
      }
    }, 2000);
  };

  const nextChallenge = () => {
    const newRecipe = generateRecipe();
    setCurrentChallenge({
      recipe: newRecipe,
      userMix: [],
      completed: false,
      score: 0
    });
    setPotions(initializePotions());
    setSelectedPotion(null);
    setPouringAmount('');
    setShowFeedback(false);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'measures',
        mode: 'potion',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setChallengeCount(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    nextChallenge();
  };

  const clearCauldron = () => {
    if (currentChallenge) {
      // Restore potion volumes
      const restoredPotions = initializePotions();
      setPotions(restoredPotions);
      
      setCurrentChallenge({
        ...currentChallenge,
        userMix: []
      });
    }
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000) {
      return `${(volume / 1000).toFixed(volume % 1000 === 0 ? 0 : 1)}L`;
    }
    return `${volume}mL`;
  };

  const getTotalUserVolume = () => {
    if (!currentChallenge) return 0;
    return currentChallenge.userMix.reduce((total, mix) => total + mix.volume, 0);
  };

  useEffect(() => {
    if (!showTutorial) {
      nextChallenge();
    }
  }, [showTutorial]);

  useEffect(() => {
    if (timerMode && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timerMode && timeRemaining === 0) {
      checkRecipe();
    }
  }, [timerMode, timeRemaining]);

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Beaker className="h-16 w-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Start Brewing!' : 'Commencer à préparer !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-lg font-medium text-purple-700 dark:text-purple-300">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`} - {language === 'en' ? `Challenge ${challengeCount + 1}/10` : `Défi ${challengeCount + 1}/10`}
          </div>
          
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Master Alchemist!' : 'Maître Alchimiste !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{score}</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Total Points' : 'Points totaux'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {language === 'en' ? 'Brew Again' : 'Préparer à nouveau'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recipe Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-yellow-500" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Magic Recipe' : 'Recette Magique'}
              </h3>
            </div>
            
            {currentChallenge && (
              <div className="space-y-4">
                <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
                  <h4 className="font-bold text-purple-800 dark:text-purple-200 mb-2">
                    {currentChallenge.recipe.name} {currentChallenge.recipe.magicEffect}
                  </h4>
                  <p className="text-purple-700 dark:text-purple-300 text-sm mb-3">
                    {currentChallenge.recipe.description}
                  </p>
                  
                  <div className="space-y-2">
                    <div className="font-medium text-purple-800 dark:text-purple-200">
                      {language === 'en' ? 'Ingredients needed:' : 'Ingrédients nécessaires :'}
                    </div>
                    {currentChallenge.recipe.ingredients.map((ingredient, index) => {
                      const potion = potions.find(p => p.id === ingredient.potionId);
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: potion?.color }}
                            ></div>
                            <span className="text-sm text-purple-700 dark:text-purple-300">
                              {potion?.name}
                            </span>
                          </div>
                          <span className="font-mono text-purple-800 dark:text-purple-200">
                            {formatVolume(ingredient.volume)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                    <div className="flex justify-between text-sm">
                      <span className="text-purple-600 dark:text-purple-400">
                        {language === 'en' ? 'Total volume:' : 'Volume total :'}
                      </span>
                      <span className="font-mono text-purple-800 dark:text-purple-200">
                        {formatVolume(currentChallenge.recipe.targetVolume)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Difficulty:' : 'Difficulté :'} 
                  <span className="ml-2">
                    {'⭐'.repeat(currentChallenge.recipe.difficulty)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Brewing Station */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Brewing Station' : 'Station de Préparation'}
              </h3>
              <button
                onClick={clearCauldron}
                className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
            
            {/* Potion Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'en' ? 'Select Potion:' : 'Sélectionner une potion :'}
              </label>
              <div className="grid grid-cols-1 gap-2">
                {potions.map((potion) => (
                  <button
                    key={potion.id}
                    onClick={() => setSelectedPotion(potion.id)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      selectedPotion === potion.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/50'
                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: potion.color }}
                        ></div>
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {potion.name}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {formatVolume(potion.currentVolume)} {language === 'en' ? 'left' : 'restant'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'en' ? 'Amount (mL):' : 'Quantité (mL) :'}
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={pouringAmount}
                  onChange={(e) => setPouringAmount(e.target.value)}
                  placeholder="250"
                  className="flex-1 p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  min="1"
                  max={selectedPotion ? potions.find(p => p.id === selectedPotion)?.currentVolume : 1000}
                />
                <button
                  onClick={addIngredient}
                  disabled={!selectedPotion || !pouringAmount || showFeedback}
                  className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {language === 'en' ? 'Pour' : 'Verser'}
                </button>
              </div>
            </div>
            
            <button
              onClick={checkRecipe}
              disabled={!currentChallenge || currentChallenge.userMix.length === 0 || showFeedback}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {language === 'en' ? 'Test Potion' : 'Tester la potion'}
            </button>
          </div>

          {/* Cauldron Visualization */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Magic Cauldron' : 'Chaudron Magique'}
            </h3>
            
            {/* Cauldron */}
            <div className="relative mx-auto w-32 h-40 border-4 border-slate-400 dark:border-slate-500 rounded-b-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
              {/* Liquid layers */}
              {currentChallenge && currentChallenge.userMix.map((mix, index) => {
                const potion = potions.find(p => p.id === mix.potionId);
                const totalVolume = getTotalUserVolume();
                const previousHeight = currentChallenge.userMix.slice(0, index).reduce((sum, m) => sum + m.volume, 0);
                const height = totalVolume > 0 ? (mix.volume / Math.max(totalVolume, currentChallenge.recipe.targetVolume)) * 100 : 0;
                
                return (
                  <div
                    key={index}
                    className="absolute bottom-0 left-0 right-0 transition-all duration-500"
                    style={{
                      backgroundColor: potion?.color,
                      height: `${height}%`,
                      bottom: `${(previousHeight / Math.max(totalVolume, currentChallenge.recipe.targetVolume)) * 100}%`,
                      opacity: 0.8
                    }}
                  />
                );
              })}
              
              {/* Bubbles effect for completed potions */}
              {showFeedback && isCorrect && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-2xl animate-bounce">✨</div>
                </div>
              )}
            </div>
            
            {/* Current Mix */}
            <div className="mt-4 space-y-2">
              <div className="font-medium text-slate-700 dark:text-slate-300">
                {language === 'en' ? 'Current Mix:' : 'Mélange actuel :'}
              </div>
              {currentChallenge && currentChallenge.userMix.map((mix, index) => {
                const potion = potions.find(p => p.id === mix.potionId);
                return (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: potion?.color }}
                      ></div>
                      <span className="text-slate-700 dark:text-slate-300">
                        {potion?.name}
                      </span>
                    </div>
                    <span className="font-mono text-slate-600 dark:text-slate-400">
                      {formatVolume(mix.volume)}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Total:' : 'Total :'}
                </span>
                <span className="font-mono text-slate-800 dark:text-slate-200">
                  {formatVolume(getTotalUserVolume())}
                </span>
              </div>
            </div>

            {showFeedback && (
              <div className={`mt-4 p-3 rounded-lg ${
                isCorrect 
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
              }`}>
                <p className="font-medium text-center">
                  {isCorrect 
                    ? (language === 'en' ? 'Perfect Potion! ✨' : 'Potion parfaite ! ✨')
                    : (language === 'en' ? 'Try Again! 🧪' : 'Réessayez ! 🧪')}
                </p>
              </div>
            )}
            
            <div className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {language === 'en' ? 'Score:' : 'Score :'} {score}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PotionLabGame;